import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { StoreProvider } from './data/store.tsx';
import { SessionProvider } from './data/session.tsx';
import { CloudSyncProvider } from './data/CloudSync.tsx';
import { App } from './ui/App.tsx';
import { applySettings, readSettings } from './ui/theme.ts';
import './styles/app.css';
import './styles/mobile.css';
import './styles/art.css';

// The theme is written onto the root before React mounts, so the first paint is
// already the chosen palette rather than a flash of the default one.
applySettings(readSettings());

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <SessionProvider>
        <StoreProvider>
          <CloudSyncProvider>
            <App />
          </CloudSyncProvider>
        </StoreProvider>
      </SessionProvider>
    </BrowserRouter>
  </StrictMode>,
);

// Nothing drains here. The outbox belongs to whoever is signed in, and a fixed
// local id sending a guest's ratings to the server on every reconnect is how
// private planning data leaves a device nobody agreed to send it from. The
// account layer starts and stops that queue against its own identity.

// Any cache written by an earlier worker can hold private API responses, and the
// Cache API keeps them until something removes them. This runs from the page, so
// it does not wait for the new worker to reach activation.
void caches
  ?.keys()
  .then((keys) =>
    Promise.all(
      keys
        .filter((k) => k.startsWith('festival-scheduler-') && !k.startsWith('festival-scheduler-static-'))
        .map((k) => caches.delete(k)),
    ),
  )
  .catch(() => undefined);

// Removes the unused festival-scheduler IndexedDB. The request is not awaited.
try {
  indexedDB.deleteDatabase('festival-scheduler');
} catch {
  // A browser without IndexedDB has nothing here to remove.
}

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  // One reload per release, so a tab left open picks up the new bundle instead
  // of running the old script against a cache the new worker has emptied.
  let reloaded = false;
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type !== 'release-activated' || reloaded) return;
    reloaded = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').then((registration) => {
      // A worker that installed while the old one held the page stays waiting.
      // Releasing it here replaces the version that cached API responses.
      const release = () => registration.waiting?.postMessage({ type: 'activate-update' });
      release();
      registration.addEventListener('updatefound', () => {
        registration.installing?.addEventListener('statechange', release);
      });
    });
  });
}
